// Las miniaturas de las celdas del lienzo: la fila (`useDatosDeCeldas`, una query por
// lote de ids) y la firma del `opt/` en lote (`firmarEnLote`), cayendo al `full/` cuando
// el thumbnail todavía no existe — el mismo camino que `useCreacionesRecientes`. El
// thumbnail de un video es el PNG homónimo: el bucket no guarda un `opt/` en mp4.
//
// El `full/` se firma en la MISMA ida que el `opt/`, para todas las celdas: es la firma
// que el visor prestado necesita para abrir sin salir a la red
// (ADR-2026-09-16-el-lienzo-abre-el-visor-con-la-fila-que-ya-tiene). Son firmas, no
// bytes, y `firmarEnLote` las deja en la caché global, de donde las lee el toque.

import { useEffect, useMemo, useState } from 'react';
import {
  useDatosDeCeldas,
  type DatosCelda,
} from '../../pages/v2/components/useDatosDeCeldas';
import { firmarEnLote } from '../../utils/firmarEnLote';

export interface Miniatura {
  src: string;
  datos: DatosCelda | undefined;
}

const nombreDeThumb = (fileName: string, esVideo: boolean): string =>
  esVideo ? fileName.replace(/\.(mp4|webm|mov)$/i, '.png') : fileName;

interface Pedido {
  id: string;
  opt: string;
  full: string;
}

export function useMiniaturasDelLienzo(ids: readonly string[], companyId: string | null): Map<string, Miniatura> {
  return useMiniaturasYBorradas(ids, companyId).miniaturas;
}

/**
 * Las miniaturas y, aparte, cuáles de esos ids son creaciones ELIMINADAS (la fila dice
 * `active = false`): el lienzo las saca en vez de dibujarlas como un recuadro gris
 * (ADR-2026-10-04-eliminar-saca-la-creacion-de-la-vista).
 */
export function useMiniaturasYBorradas(
  ids: readonly string[],
  companyId: string | null,
): { miniaturas: Map<string, Miniatura>; borradas: ReadonlySet<string> } {
  const datos = useDatosDeCeldas(ids, companyId);
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  // Qué paths hacen falta, derivado de las filas que ya llegaron y todavía sin URL.
  const pedidos = useMemo<Pedido[]>(() => {
    if (!companyId) return [];
    const out: Pedido[] = [];
    for (const id of ids) {
      if (urls.has(id)) continue;
      const d = datos.get(id);
      if (!d?.fileName) continue;
      const esVideo = d.tipo === 'video';
      out.push({
        id,
        opt: `${companyId}/opt/${nombreDeThumb(d.fileName, esVideo)}`,
        full: `${companyId}/full/${d.fileName}`,
      });
    }
    return out;
  }, [ids, datos, companyId, urls]);
  // Resume `pedidos`: mismos ids → mismo trabajo, sin volver a firmar en cada render.
  const clave = pedidos.map((p) => p.id).join('|');

  useEffect(() => {
    if (pedidos.length === 0) return;
    let vivo = true;
    (async () => {
      const firmas = await firmarEnLote([...pedidos.map((p) => p.opt), ...pedidos.map((p) => p.full)]);
      if (!vivo) return;
      setUrls((prev) => {
        const next = new Map(prev);
        for (const p of pedidos) {
          const u = firmas.get(p.opt) ?? firmas.get(p.full);
          if (u) next.set(p.id, u);
        }
        return next;
      });
    })().catch(() => {});
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const miniaturas = useMemo(() => {
    const out = new Map<string, Miniatura>();
    for (const id of ids) {
      const src = urls.get(id);
      if (src) out.set(id, { src, datos: datos.get(id) });
    }
    return out;
  }, [ids, urls, datos]);
  const borradas = useMemo(() => {
    const out = new Set<string>();
    for (const id of ids) if (datos.get(id)?.activo === false) out.add(id);
    return out;
  }, [ids, datos]);
  return useMemo(() => ({ miniaturas, borradas }), [miniaturas, borradas]);
}
